import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { buildEtiquetaHTML, openPrint } from "@/lib/print";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusTimeline, StatusBadge, OccurrenceBadges } from "@/components/StatusUI";
import FastScanInput from "@/components/FastScanInput";
import { fmtDate, STATUS, AUDIT_LABELS, NAO_APROVACAO_FALLBACK, CORTE_FALLBACK } from "@/lib/meta";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, ArrowRight, Scissors, Printer, Check, XCircle, Package, Truck, Shuffle, Undo2, Trash2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const CAN_OPERATE = ["ADMIN", "GESTOR", "SUPERVISOR", "ESTOQUISTA", "SUPER_ADMIN", "MASTER"];

const NEXT_ACTION = {
  SOLICITADO: "Aprovar itens", APROVADO: "Iniciar Separação", EM_SEPARACAO: "Bipar itens e Finalizar Separação",
  SEPARADO: "Iniciar Conferência", EM_CONFERENCIA: "Bipar itens e Finalizar Conferência",
  CONFERIDO: "Expedir (gerar etiqueta)", EXPEDIDO: "Embarcar → Em Trânsito",
  EM_TRANSITO: "Receber na Unidade", RECEBIDO: "Concluir Pedido", CONCLUIDO: "Fluxo concluído", CANCELADO: "—",
};

export default function PedidoDetalhe() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [aprovModal, setAprovModal] = useState(false);
  const [aprov, setAprov] = useState({});   // {item_id: {qtd, motivo, obs}}
  const [corteModal, setCorteModal] = useState(false);
  const [corte, setCorte] = useState({});   // {item_id: {motivo, obs}}
  const [labelModal, setLabelModal] = useState(false);
  const [etqFmt, setEtqFmt] = useState("TERMICA");
  const [etqSub, setEtqSub] = useState("ETIQUETA"); // Mini 58: ETIQUETA (57x50) | CONTINUA (bobina)
  const [etqUseVol, setEtqUseVol] = useState(false); // quantidade como volumes 1/N
  const [etqPerSheet, setEtqPerSheet] = useState(4);
  const [etqCopies, setEtqCopies] = useState(1);
  const qrRef = useRef(null);
  const [flash, setFlash] = useState("");
  const [alerta, setAlerta] = useState("");
  const [unitName, setUnitName] = useState("");
  const [trilha, setTrilha] = useState([]);
  const [subs, setSubs] = useState({});     // {item_id: [{sku,nome,disponivel}]}
  const [motNA, setMotNA] = useState(NAO_APROVACAO_FALLBACK);
  const [motCorte, setMotCorte] = useState(CORTE_FALLBACK);
  const [adminAct, setAdminAct] = useState(null);   // {type: 'voltar'|'cancelar'|'excluir'}
  const [adminMotivo, setAdminMotivo] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/pedidos/${id}`);
    setP(data);
    const u = await api.get("/units");
    setUnitName((u.data.find((x) => x.id === data.unit_id) || {}).nome || "");
    try { const a = await api.get(`/pedidos/${id}/auditoria`); setTrilha(a.data.eventos || []); } catch (_) {}
    if (data.status === "EM_SEPARACAO") {
      const map = {};
      await Promise.all(data.itens.map(async (it) => {
        try { const r = await api.get(`/pedidos/${id}/item/${it.id}/substitutos`); map[it.id] = r.data.substitutos || []; } catch (_) { map[it.id] = []; }
      }));
      setSubs(map);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/motivos?tipo=NAO_APROVACAO").then((r) => { if (r.data.length) setMotNA(r.data.filter((m) => m.ativo).map((m) => m.label)); }).catch(() => {});
    api.get("/motivos?tipo=CORTE").then((r) => { if (r.data.length) setMotCorte(r.data.filter((m) => m.ativo).map((m) => m.label)); }).catch(() => {});
  }, []);

  const act = async (fn) => {
    setBusy(true);
    try { await fn(); await load(); }
    catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : "Erro na operação"); }
    finally { setBusy(false); }
  };
  const flashScreen = (ok) => { setFlash(ok ? "scan-flash-ok" : "scan-flash-err"); setTimeout(() => setFlash(""), 500); };

  const runAdmin = async () => {
    const m = adminMotivo.trim();
    const t = adminAct?.type;
    if ((t === "cancelar" || t === "voltar") && !m) return toast.error("Informe o motivo");
    setBusy(true);
    try {
      if (t === "cancelar") { await api.post(`/pedidos/${id}/cancelar`, { motivo: m }); toast.success("Pedido cancelado"); }
      else if (t === "voltar") { const { data } = await api.post(`/pedidos/${id}/admin/voltar-etapa`, { motivo: m }); toast.success(`Retornado: ${STATUS[data.de]?.label || data.de} → ${STATUS[data.para]?.label || data.para}`); }
      else if (t === "excluir") { await api.delete(`/pedidos/${id}`); toast.success("Pedido excluído"); setAdminAct(null); setBusy(false); return nav("/pedidos"); }
      setAdminAct(null); setAdminMotivo(""); await load();
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : "Erro na ação administrativa"); }
    finally { setBusy(false); }
  };

  const imprimirEtiqueta = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    const qr = canvas ? canvas.toDataURL("image/png") : "";
    openPrint(buildEtiquetaHTML({
      numero: p.numero, destino: unitName, origem: p.cd_nome || "CD", data: fmtDate(p.created_at),
      payload: p.qr_payload, qr, formato: etqFmt, subtipo: etqSub, perSheet: etqPerSheet, copies: etqCopies,
      volumes: etqUseVol ? etqCopies : null, tipoCompl: p.tipo === "COMPLEMENTAR",
    }));
  };

  if (!p) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const canOp = CAN_OPERATE.includes(user.role);
  const st = p.status;

  const openAprov = () => {
    const init = {};
    p.itens.forEach((it) => { init[it.id] = { qtd: it.qtd_solicitada, motivo: "", obs: "" }; });
    setAprov(init);
    setAprovModal(true);
  };
  const confirmAprov = () => act(async () => {
    for (const it of p.itens) {
      const a = aprov[it.id] || {};
      const na = it.qtd_solicitada - Number(a.qtd);
      if (na > 0 && !a.motivo) return toast.error(`Informe o motivo da não aprovação de "${it.nome}"`);
      if (na > 0 && a.motivo === "Outro" && !(a.obs || "").trim()) return toast.error(`Para "Outro", informe a observação em "${it.nome}"`);
    }
    await api.post(`/pedidos/${id}/aprovar`, {
      itens: p.itens.map((it) => ({ item_id: it.id, qtd_aprovada: Number(aprov[it.id]?.qtd),
        motivo_nao_aprovacao: aprov[it.id]?.motivo || "", observacao: aprov[it.id]?.obs || "" })),
    });
    setAprovModal(false);
    const na = p.itens.filter((it) => Number(aprov[it.id]?.qtd) < it.qtd_solicitada).length;
    toast.success(na ? `Pedido aprovado (aprovação parcial em ${na} item(ns))` : "Pedido aprovado integralmente");
  });

  const bipar = (endpoint) => async ({ codigo, quantidade, is_master }) => {
    try {
      const { data } = await api.post(`/pedidos/${id}/${endpoint}`, { codigo, quantidade, is_master });
      flashScreen(true);
      const sub = data.substituicao ? "SUBSTITUTO ⇄ " : "";
      toast.success(`${sub}${data.nome}: ${data.feito}/${data.alvo}${data.completo ? " — COMPLETO ✓" : ` · faltam ${data.restante}`}`);
      load();
    } catch (e) {
      flashScreen(false);
      const msg = e.response?.data?.detail;
      const txt = typeof msg === "string" ? msg : "Bipagem inválida";
      if (txt.includes("QUANTIDADE JÁ ATINGIDA")) { setAlerta(txt); setTimeout(() => setAlerta(""), 2600); }
      toast.error(txt);
    }
  };

  // Finalizar separação — se houver corte (aprovado>separado), exige motivo
  const clickFinalizarSep = () => {
    const comCorte = p.itens.filter((it) => (it.qtd_aprovada - it.qtd_separada) > 0);
    if (!comCorte.length) return act(() => api.post(`/pedidos/${id}/separacao/finalizar`, { cortes: [] }));
    const init = {};
    comCorte.forEach((it) => { init[it.id] = { motivo: "", obs: "" }; });
    setCorte(init);
    setCorteModal(true);
  };
  const confirmCorte = () => act(async () => {
    const cortes = [];
    for (const it of p.itens) {
      if ((it.qtd_aprovada - it.qtd_separada) > 0) {
        const c = corte[it.id] || {};
        if (!c.motivo) return toast.error(`Informe o motivo do corte de "${it.nome}"`);
        if (c.motivo === "Outro" && !(c.obs || "").trim()) return toast.error(`Para "Outro", informe a observação do corte de "${it.nome}"`);
        cortes.push({ item_id: it.id, motivo: c.motivo, observacao: c.obs || "" });
      }
    }
    await api.post(`/pedidos/${id}/separacao/finalizar`, { cortes });
    setCorteModal(false);
    toast.success(`Separação finalizada — ${cortes.length} corte(s), complementar gerado`);
  });

  const Actions = () => {
    if (!canOp && st !== "EM_TRANSITO") return null;
    const btns = [];
    if (st === "SOLICITADO" && canOp) btns.push(<Button key="ap" onClick={openAprov} disabled={busy} data-testid="btn-aprovar"><Check className="w-4 h-4" /> Aprovar itens</Button>);
    if (st === "APROVADO" && canOp) btns.push(<Button key="is" onClick={() => act(() => api.post(`/pedidos/${id}/separacao/iniciar`))} disabled={busy} data-testid="btn-iniciar-separacao"><Package className="w-4 h-4" /> Iniciar Separação</Button>);
    if (st === "SEPARADO" && canOp) btns.push(<Button key="ic" onClick={() => act(() => api.post(`/pedidos/${id}/conferencia/iniciar`))} disabled={busy} data-testid="btn-iniciar-conferencia"><ArrowRight className="w-4 h-4" /> Iniciar Conferência</Button>);
    if (st === "CONFERIDO" && canOp) btns.push(<Button key="ex" onClick={() => act(async () => { await api.post(`/pedidos/${id}/expedir`); setLabelModal(true); })} disabled={busy} data-testid="btn-expedir"><Printer className="w-4 h-4" /> Expedir (gerar etiqueta)</Button>);
    if (st === "EXPEDIDO" && canOp) {
      btns.push(<Button key="lb" variant="outline" onClick={() => setLabelModal(true)} data-testid="btn-etiqueta"><Printer className="w-4 h-4" /> Ver Etiqueta</Button>);
      btns.push(<Button key="em" onClick={() => act(() => api.post(`/pedidos/${id}/embarcar`))} disabled={busy} data-testid="btn-embarcar"><ArrowRight className="w-4 h-4" /> Embarcar → Em Trânsito</Button>);
    }
    if (st === "EM_TRANSITO") btns.push(<Button key="rc" onClick={() => nav("/recebimento")} data-testid="btn-ir-recebimento"><ArrowRight className="w-4 h-4" /> Ir para Recebimento</Button>);
    if (st === "RECEBIDO") btns.push(<Button key="cc" onClick={() => act(() => api.post(`/pedidos/${id}/concluir`))} disabled={busy} data-testid="btn-concluir"><Check className="w-4 h-4" /> Concluir Pedido</Button>);
    return <div className="flex flex-wrap gap-2">{btns}</div>;
  };

  const showSep = st === "EM_SEPARACAO" && canOp;
  const showConf = st === "EM_CONFERENCIA" && canOp;
  const canAdminActions = ["SUPER_ADMIN", "MASTER", "GESTOR"].includes(user.role);
  const canExcluirPedido = ["SUPER_ADMIN", "MASTER"].includes(user.role);

  return (
    <div className={`space-y-5 ${flash} rounded-lg`} data-testid="pedido-detalhe">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <button onClick={() => nav("/pedidos")} className="text-xs text-muted-foreground hover:text-primary">← Pedidos</button>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase flex items-center gap-2">
            {p.numero}
            {p.tipo === "COMPLEMENTAR" && <span className="text-sm bg-amber-500/20 text-amber-200 border border-amber-500/50 px-2 py-0.5 rounded-sm font-mono">Complementar</span>}
          </h1>
          <div className="mt-1.5 inline-flex items-center gap-2 rounded-md border border-primary/50 bg-primary/10 px-3 py-1.5" data-testid="destino-destaque">
            <Truck className="w-5 h-5 text-primary shrink-0" />
            <div>
              <div className="text-[9px] uppercase text-muted-foreground leading-none tracking-wide">Unidade destino</div>
              <div className="font-display text-lg font-extrabold uppercase leading-tight">{unitName || "—"}</div>
            </div>
          </div>
          <div className="text-xs text-muted-foreground mt-1">{fmtDate(p.created_at)}</div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <StatusBadge status={st} testId="status-badge" />
          {p.aprovacao_parcial && !["CANCELADO"].includes(st) && (
            <span className="text-[10px] uppercase font-bold bg-amber-500/20 text-amber-200 border border-amber-500/50 px-2 py-0.5 rounded-sm font-mono" data-testid="selo-aprovacao-parcial">Aprovação Parcial</span>
          )}
        </div>
      </div>

      {p.ocorrencias?.filter((o) => o.status === "ABERTA").length > 0 && (
        <OccurrenceBadges tipos={[...new Set(p.ocorrencias.filter((o) => o.status === "ABERTA").map((o) => o.tipo))]} />
      )}

      <Card className="bg-card/70 p-4 overflow-x-auto"><StatusTimeline current={st} /></Card>

      <Card className="bg-card/70 p-4 flex flex-wrap items-center gap-x-8 gap-y-2" data-testid="proxima-acao">
        <div><div className="text-[10px] uppercase text-muted-foreground">Status atual</div><div className="font-display text-lg font-bold text-foreground uppercase">{STATUS[st]?.label || st}</div></div>
        <div><div className="text-[10px] uppercase text-muted-foreground">Próxima ação</div><div className="font-display text-lg font-bold text-primary uppercase">{NEXT_ACTION[st] || "—"}</div></div>
      </Card>

      <Card className="bg-card/70 p-4"><Actions /></Card>

      {canAdminActions && !["CONCLUIDO"].includes(st) && (
        <Card className="bg-card/70 p-4 border-amber-600/40" data-testid="admin-actions">
          <div className="text-[11px] uppercase tracking-wide text-amber-300 font-bold mb-2 flex items-center gap-1"><ShieldAlert className="w-4 h-4" /> Ações administrativas</div>
          <div className="flex flex-wrap gap-2">
            {!["SOLICITADO", "RECEBIDO", "CONCLUIDO", "CANCELADO"].includes(st) && (
              <Button variant="outline" size="sm" onClick={() => { setAdminMotivo(""); setAdminAct({ type: "voltar" }); }} disabled={busy} data-testid="btn-admin-voltar"><Undo2 className="w-4 h-4" /> Retornar à etapa anterior</Button>
            )}
            {!["CANCELADO"].includes(st) && (
              <Button variant="outline" size="sm" className="text-red-400 border-red-500/40" onClick={() => { setAdminMotivo(""); setAdminAct({ type: "cancelar" }); }} disabled={busy} data-testid="btn-admin-cancelar"><XCircle className="w-4 h-4" /> Cancelar pedido</Button>
            )}
            {canExcluirPedido && ["SOLICITADO", "APROVADO", "CANCELADO"].includes(st) && (
              <Button variant="outline" size="sm" onClick={() => { setAdminMotivo(""); setAdminAct({ type: "excluir" }); }} disabled={busy} data-testid="btn-admin-excluir"><Trash2 className="w-4 h-4" /> Excluir pedido</Button>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground mt-2">Retornos e cancelamentos executam estorno compensatório quando há movimentação, preservando o histórico e o ledger. Exclusão só é permitida sem movimentações vinculadas.</div>
        </Card>
      )}

      <Dialog open={!!adminAct} onOpenChange={(v) => !v && setAdminAct(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display uppercase">
            {adminAct?.type === "voltar" ? "Retornar à etapa anterior" : adminAct?.type === "excluir" ? "Excluir pedido" : "Cancelar pedido"}
          </DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            {adminAct?.type === "excluir" ? (
              <div className="text-muted-foreground">Só é possível excluir pedidos sem movimentações vinculadas (ledger, complementar, ocorrências). Havendo movimentação, utilize Cancelar/Estorno. Confirmar exclusão de <b>{p.numero}</b>?</div>
            ) : (
              <>
                <div className="text-muted-foreground">Motivo obrigatório — registrado na auditoria com seu usuário e data/hora.</div>
                <Textarea value={adminMotivo} onChange={(e) => setAdminMotivo(e.target.value)} placeholder="Descreva o motivo" data-testid="admin-motivo" />
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdminAct(null)}>Voltar</Button>
            <Button onClick={runAdmin} disabled={busy} data-testid="admin-confirmar">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {(showSep || showConf) && (
        <FastScanInput label={showSep ? "Bipar Separação (aceita substitutos autorizados)" : "Bipar Conferência"}
          onScan={bipar(showSep ? "separacao/bipar" : "conferencia/bipar")} testid={showSep ? "sep-scan" : "conf-scan"} />
      )}

      {(showSep || showConf) && (
        <Card className="bg-card/70 p-4" data-testid="op-card">
          {alerta && <div className="mb-3 rounded-md border-2 border-red-500 bg-red-950/70 text-red-200 font-bold uppercase text-center py-2.5 animate-pulse" data-testid="op-alerta">⚠ {alerta}</div>}
          <div className="font-display text-lg font-bold uppercase mb-3">{showSep ? "Separação" : "Conferência"}</div>
          <div className="space-y-2">
            {p.itens.map((it) => {
              const alvo = showSep ? it.qtd_aprovada : it.qtd_separada;
              const feito = showSep ? it.qtd_separada : it.qtd_conferida;
              const falta = Math.max(0, alvo - feito);
              const completo = alvo > 0 && feito >= alvo;
              const itemSubs = (subs[it.id] || []).filter((s) => s.disponivel > 0);
              return (
                <div key={it.id} className={`rounded-md border p-3 ${completo ? "border-emerald-500/60 bg-emerald-950/30" : "border-border"}`} data-testid={`op-item-${it.sku}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0"><div className="font-semibold text-sm truncate">{it.nome}</div><div className="text-[11px] font-mono text-muted-foreground">{it.sku}</div></div>
                    {completo
                      ? <span className="text-[11px] font-bold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-500/50 px-2 py-1 rounded-sm font-mono flex items-center gap-1" data-testid={`op-completo-${it.sku}`}><Check className="w-3.5 h-3.5" /> Completo</span>
                      : <span className="font-mono font-extrabold text-2xl text-amber-300">{feito}<span className="text-muted-foreground text-lg">/{alvo}</span></span>}
                  </div>
                  {/* composição da separação (SKUs físicos usados, incl. substitutos) */}
                  {(it.separacoes || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`op-composicao-${it.sku}`}>
                      {it.separacoes.map((l) => (
                        <span key={l.id} className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${l.substituicao ? "bg-sky-500/15 text-sky-200 border-sky-500/40" : "bg-secondary/60 text-muted-foreground border-border"}`}>
                          {l.substituicao && <Shuffle className="w-3 h-3 inline mr-0.5" />}{l.sku} · {l.quantidade}
                        </span>
                      ))}
                    </div>
                  )}
                  {/* substitutos disponíveis (evitar corte) */}
                  {showSep && !completo && itemSubs.length > 0 && (
                    <div className="mt-2 rounded-md border border-sky-500/40 bg-sky-950/30 p-2" data-testid={`op-subs-${it.sku}`}>
                      <div className="text-[10px] uppercase font-bold text-sky-300 flex items-center gap-1"><Shuffle className="w-3 h-3" /> Substitutos disponíveis</div>
                      {itemSubs.map((s) => (
                        <div key={s.sku_id} className="text-[11px] text-sky-100/90 flex justify-between"><span>{s.nome} <span className="font-mono opacity-70">({s.ean})</span></span><span className="font-mono">{s.disponivel} disp.</span></div>
                      ))}
                      <div className="text-[10px] text-muted-foreground mt-0.5">Bipe o código do substituto para atender sem corte.</div>
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-2 mt-2.5 text-center">
                    <div className="bg-secondary/40 rounded p-1.5"><div className="text-[9px] uppercase text-muted-foreground">{showSep ? "A separar" : "Esperado"}</div><div className="font-mono font-bold">{alvo}</div></div>
                    <div className="bg-secondary/40 rounded p-1.5"><div className="text-[9px] uppercase text-muted-foreground">{showSep ? "Separado" : "Conferido"}</div><div className="font-mono font-bold text-emerald-300">{feito}</div></div>
                    <div className="bg-secondary/40 rounded p-1.5"><div className="text-[9px] uppercase text-muted-foreground">Faltante</div><div className={`font-mono font-bold ${falta > 0 ? "text-red-300" : "text-muted-foreground"}`}>{falta}</div></div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
      {showSep && <Button variant="secondary" className="w-full h-12" onClick={clickFinalizarSep} disabled={busy} data-testid="btn-finalizar-separacao">Finalizar Separação</Button>}
      {showConf && <Button variant="secondary" className="w-full h-12" onClick={() => act(() => api.post(`/pedidos/${id}/conferencia/finalizar`))} data-testid="btn-finalizar-conferencia">Finalizar Conferência</Button>}

      {/* Itens */}
      <Card className="bg-card/70 p-4">
        <div className="font-display text-lg font-bold uppercase mb-3">Itens</div>
        <div className="space-y-2">
          {p.itens.map((it) => (
            <div key={it.id} className="border border-border rounded-md p-3" data-testid={`item-${it.sku}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <div className="font-semibold text-sm">{it.nome}</div>
                  <div className="text-[11px] text-muted-foreground font-mono">{it.sku} · {it.marca} · EAN {it.ean}</div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {it.qtd_nao_aprovada > 0 && <span className="text-[10px] uppercase font-bold bg-amber-500/20 text-amber-200 border border-amber-500/50 px-1.5 py-0.5 rounded-sm font-mono" data-testid={`nao-aprovado-${it.sku}`}>Não aprovado {it.qtd_nao_aprovada}</span>}
                  {it.qtd_cortada > 0 && <span className="text-[10px] uppercase font-bold bg-red-500/20 text-red-300 border border-red-500/50 px-1.5 py-0.5 rounded-sm font-mono flex items-center gap-1" data-testid={`corte-${it.sku}`}><Scissors className="w-3 h-3" /> Corte {it.qtd_cortada}</span>}
                </div>
              </div>
              {(it.motivo_nao_aprovacao || it.motivo_corte) && (
                <div className="mt-1.5 text-[11px] text-muted-foreground space-y-0.5">
                  {it.motivo_nao_aprovacao && <div>Não aprovação: <span className="text-amber-200">{it.motivo_nao_aprovacao}</span>{it.obs_nao_aprovacao ? ` — ${it.obs_nao_aprovacao}` : ""}</div>}
                  {it.motivo_corte && <div>Corte: <span className="text-red-300">{it.motivo_corte}</span></div>}
                </div>
              )}
              {(it.separacoes || []).length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {it.separacoes.map((l) => (
                    <span key={l.id} className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${l.substituicao ? "bg-sky-500/15 text-sky-200 border-sky-500/40" : "bg-secondary/60 text-muted-foreground border-border"}`}>
                      {l.substituicao && <Shuffle className="w-3 h-3 inline mr-0.5" />}{l.sku} · {l.quantidade}
                    </span>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-3 sm:grid-cols-7 gap-2 mt-3 text-center">
                {[["Solicitado", it.qtd_solicitada], ["Aprovado", it.qtd_aprovada], ["Não aprov.", it.qtd_nao_aprovada || 0], ["Separado", it.qtd_separada], ["Cortado", it.qtd_cortada || 0], ["Expedido", it.qtd_expedida], ["Recebido", it.qtd_recebida]].map(([l, v]) => (
                  <div key={l} className="bg-secondary/50 rounded p-1.5"><div className="text-[9px] uppercase text-muted-foreground">{l}</div><div className="font-mono font-bold">{v}</div></div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Trilha de Auditoria */}
      <Card className="bg-card/70 p-4" data-testid="trilha-auditoria">
        <div className="font-display text-lg font-bold uppercase mb-3">Trilha de Auditoria</div>
        <div className="space-y-1.5">
          {trilha.map((e, i) => {
            const grpCls = { STATUS: "border-primary/50 text-primary", AUDITORIA: "border-slate-500/50 text-slate-300", ESTOQUE: "border-emerald-500/50 text-emerald-300", OCORRENCIA: "border-rose-500/50 text-rose-300" }[e.grupo] || "border-border text-muted-foreground";
            const label = AUDIT_LABELS[e.acao] || e.acao;
            const d = e.detalhe || {};
            let extra = "";
            if (e.grupo === "ESTOQUE") { const parts = Object.entries(d.deltas || {}).map(([k, v]) => `${k} ${v > 0 ? "+" : ""}${v}`); extra = `${d.produto || d.sku || ""} · ${parts.join(", ")}${d.origem || d.destino ? ` · ${d.origem || "?"} → ${d.destino || "?"}` : ""}`; }
            else if (e.grupo === "OCORRENCIA") { extra = [d.produto, d.quantidade != null ? `qtd ${d.quantidade}` : "", d.nota].filter(Boolean).join(" · "); }
            else if (e.grupo === "AUDITORIA") { extra = [d.sku && `${d.sku}`, d.substituto ? "substituto" : "", d.de != null ? `${d.de}→${d.para}` : "", d.cortes ? `${d.cortes} corte(s)` : ""].filter(Boolean).join(" · "); }
            return (
              <div key={i} className="flex items-start justify-between gap-2 text-xs border-b border-border pb-1.5" data-testid={`trilha-item-${i}`}>
                <div className="flex items-start gap-2 min-w-0">
                  <span className={`shrink-0 rounded-sm border px-1.5 py-0.5 text-[9px] font-bold uppercase font-mono ${grpCls}`}>{e.grupo}</span>
                  <div className="min-w-0"><div className="font-medium">{label}</div>{extra && <div className="text-[11px] text-muted-foreground truncate">{extra}</div>}</div>
                </div>
                <span className="text-muted-foreground shrink-0 text-right">{e.usuario || "—"}<br />{fmtDate(e.timestamp)}</span>
              </div>
            );
          })}
          {!trilha.length && <div className="text-xs text-muted-foreground">Sem eventos registrados.</div>}
        </div>
      </Card>

      {/* Modal Aprovação */}
      <Dialog open={aprovModal} onOpenChange={setAprovModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display uppercase">Aprovação de Itens</DialogTitle></DialogHeader>
          <div className="text-xs text-muted-foreground -mt-2">Defina quanto autorizar. A quantidade não aprovada é uma decisão administrativa (não é corte de estoque e não gera complementar).</div>
          <div className="space-y-3 max-h-[55vh] overflow-y-auto">
            {p.itens.map((it) => {
              const a = aprov[it.id] || { qtd: it.qtd_solicitada, motivo: "", obs: "" };
              const na = it.qtd_solicitada - Number(a.qtd);
              const setA = (patch) => setAprov((prev) => ({ ...prev, [it.id]: { ...a, ...patch } }));
              return (
                <div key={it.id} className="border border-border rounded p-3">
                  <div className="text-sm font-medium">{it.nome}</div>
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    <span className="text-xs text-muted-foreground">Solicitado <b className="font-mono text-foreground">{it.qtd_solicitada}</b></span>
                    <span className="text-xs text-muted-foreground">Aprovar</span>
                    <Input type="number" min={0} max={it.qtd_solicitada} value={a.qtd}
                      onChange={(e) => setA({ qtd: e.target.value })} className="w-24 h-9 font-mono text-center" data-testid={`aprov-qtd-${it.sku}`} />
                    {na > 0 && <span className="text-xs text-amber-300 font-mono" data-testid={`aprov-na-${it.sku}`}>Não aprovado {na}</span>}
                  </div>
                  {na > 0 && (
                    <div className="mt-2 space-y-2">
                      <Select value={a.motivo} onValueChange={(v) => setA({ motivo: v })}>
                        <SelectTrigger className="h-9" data-testid={`aprov-motivo-${it.sku}`}><SelectValue placeholder="Motivo da não aprovação *" /></SelectTrigger>
                        <SelectContent>{motNA.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                      </Select>
                      {a.motivo === "Outro" && <Textarea value={a.obs} onChange={(e) => setA({ obs: e.target.value })} placeholder="Observação (obrigatória para 'Outro')" className="text-sm" data-testid={`aprov-obs-${it.sku}`} />}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAprovModal(false)}>Cancelar</Button>
            <Button onClick={confirmAprov} disabled={busy} data-testid="btn-confirmar-aprovacao">Confirmar Aprovação</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Corte (na finalização da separação) */}
      <Dialog open={corteModal} onOpenChange={setCorteModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display uppercase text-red-300">Corte de Estoque</DialogTitle></DialogHeader>
          <div className="text-xs text-muted-foreground -mt-2">Estes itens não foram totalmente atendidos na separação. Informe o motivo do corte — um pedido complementar será gerado apenas com a quantidade cortada.</div>
          <div className="space-y-3 max-h-[55vh] overflow-y-auto">
            {p.itens.filter((it) => (it.qtd_aprovada - it.qtd_separada) > 0).map((it) => {
              const c = corte[it.id] || { motivo: "", obs: "" };
              const setC = (patch) => setCorte((prev) => ({ ...prev, [it.id]: { ...c, ...patch } }));
              const q = it.qtd_aprovada - it.qtd_separada;
              return (
                <div key={it.id} className="border border-red-500/40 bg-red-950/20 rounded p-3">
                  <div className="flex items-center justify-between"><div className="text-sm font-medium">{it.nome}</div><span className="text-xs font-mono text-red-300">Corte {q}</span></div>
                  <div className="text-[11px] font-mono text-muted-foreground">Aprovado {it.qtd_aprovada} · Separado {it.qtd_separada}</div>
                  <div className="mt-2 space-y-2">
                    <Select value={c.motivo} onValueChange={(v) => setC({ motivo: v })}>
                      <SelectTrigger className="h-9" data-testid={`corte-motivo-${it.sku}`}><SelectValue placeholder="Motivo do corte *" /></SelectTrigger>
                      <SelectContent>{motCorte.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                    {c.motivo === "Outro" && <Textarea value={c.obs} onChange={(e) => setC({ obs: e.target.value })} placeholder="Observação (obrigatória para 'Outro')" className="text-sm" data-testid={`corte-obs-${it.sku}`} />}
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCorteModal(false)}>Voltar</Button>
            <Button className="bg-red-500 hover:bg-red-400 text-white" onClick={confirmCorte} disabled={busy} data-testid="btn-confirmar-corte">Finalizar com corte</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Etiqueta */}
      <Dialog open={labelModal} onOpenChange={setLabelModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display uppercase">Etiqueta de Expedição</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-[10px] uppercase text-muted-foreground mb-1">Formato de impressão</div>
              <div className="flex gap-2">{[["TERMICA", "Térmica 100×150"], ["MINI58", "Mini 58 mm"], ["A4", "Folha A4"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setEtqFmt(v)} data-testid={`etq-fmt-${v}`} className={`flex-1 text-xs font-semibold px-3 py-2 rounded-md border ${etqFmt === v ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground"}`}>{l}</button>
              ))}</div>
            </div>
            {etqFmt === "TERMICA" && (
              <div className="text-xs text-muted-foreground">Tamanho: <b className="text-foreground">100 × 150 mm</b> · 1 etiqueta por página.</div>
            )}
            {etqFmt === "MINI58" && (
              <div>
                <div className="text-[10px] uppercase text-muted-foreground mb-1">Tipo de papel (Knup KP-1025 · 58 mm)</div>
                <div className="flex gap-2">{[["ETIQUETA", "Etiqueta 57×50"], ["CONTINUA", "Bobina contínua 58 mm"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setEtqSub(v)} data-testid={`etq-sub-${v}`} className={`flex-1 text-xs font-semibold px-3 py-2 rounded-md border ${etqSub === v ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground"}`}>{l}</button>
                ))}</div>
                <div className="text-[11px] text-muted-foreground mt-1">Largura útil ~48 mm · QR grande centralizado. {etqSub === "CONTINUA" ? "Altura automática, corte na serrilha." : "Etiqueta adesiva pré-cortada."}</div>
              </div>
            )}
            {etqFmt === "A4" && (
              <div><div className="text-[10px] uppercase text-muted-foreground mb-1">Etiquetas por folha A4</div>
                <div className="flex gap-2">{[1, 2, 4].map((n) => (
                  <button key={n} type="button" onClick={() => setEtqPerSheet(n)} data-testid={`etq-persheet-${n}`} className={`flex-1 text-sm font-mono px-3 py-2 rounded-md border ${etqPerSheet === n ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground"}`}>{n}</button>
                ))}</div>
              </div>
            )}
            <div>
              <div className="text-[10px] uppercase text-muted-foreground mb-1">{etqUseVol ? "Quantidade de volumes (caixas)" : "Quantidade de etiquetas"}</div>
              <div className="flex items-center gap-2">
                <Button type="button" size="icon" variant="outline" className="h-9 w-9" onClick={() => setEtqCopies((q) => Math.max(1, q - 1))} data-testid="etq-minus">−</Button>
                <Input type="number" min={1} value={etqCopies} onChange={(e) => setEtqCopies(Math.max(1, Number(e.target.value) || 1))} className="w-20 h-9 text-center font-mono" data-testid="etq-copies" />
                <Button type="button" size="icon" variant="outline" className="h-9 w-9" onClick={() => setEtqCopies((q) => q + 1)} data-testid="etq-plus">+</Button>
                {etqFmt === "A4" && !etqUseVol && <span className="text-[11px] text-muted-foreground">= {Math.ceil(etqCopies / etqPerSheet)} folha(s)</span>}
              </div>
              <label className="flex items-center gap-2 mt-2 text-xs text-muted-foreground cursor-pointer select-none">
                <input type="checkbox" checked={etqUseVol} onChange={(e) => setEtqUseVol(e.target.checked)} data-testid="etq-usevol" className="accent-primary w-4 h-4" />
                Usar como volumes (imprime <b className="text-foreground">1/{etqCopies}, 2/{etqCopies}…</b> em vez de cópias idênticas)
              </label>
            </div>
            <div className="text-[10px] uppercase text-muted-foreground text-center">Pré-visualização {etqFmt === "MINI58" ? (etqSub === "CONTINUA" ? "· Bobina 58 mm" : "· Etiqueta 57×50 mm") : etqFmt === "TERMICA" ? "· 100×150 mm" : "· A4"}</div>
            <div ref={qrRef} className={`bg-white text-black rounded-md p-3 mx-auto ${etqFmt === "MINI58" ? "w-40" : "w-52"}`} data-testid="etiqueta">
              <div className={`flex items-center justify-between border-b-2 border-black pb-1 ${etqFmt === "MINI58" ? "flex-col gap-0.5 border-b" : ""}`}>
                <div className={`font-display font-extrabold uppercase ${etqFmt === "MINI58" ? "text-xs" : "text-base"}`}>⚡ NEXO LOG</div>
                <div className="text-[10px] font-mono font-bold">{p.numero}</div>
              </div>
              <div className="text-[8px] text-neutral-500 mt-1 text-center">DESTINO</div>
              <div className={`font-extrabold uppercase leading-none text-center ${etqFmt === "MINI58" ? "text-base" : "text-xl"}`}>{unitName || "—"}</div>
              <div className="flex justify-center py-1.5"><QRCodeCanvas value={p.qr_payload} size={etqFmt === "MINI58" ? 104 : 120} level="M" includeMargin /></div>
              <div className="text-[9px] text-center">{etqFmt === "MINI58" ? `${p.cd_nome || "CD"} → ${unitName || "Unidade"}` : `Origem: ${p.cd_nome || "CD"} · ${fmtDate(p.created_at)}`}</div>
              {etqUseVol && <div className="text-center mt-1"><span className="inline-block border-2 border-black rounded px-2 py-0.5 text-[10px] font-mono font-extrabold">VOLUME 1/{etqCopies}</span></div>}
              <div className="text-center text-[8px] font-mono break-all mt-0.5">{p.qr_payload}</div>
            </div>
          </div>
          <Button onClick={imprimirEtiqueta} data-testid="btn-imprimir"><Printer className="w-4 h-4" /> Imprimir {etqFmt === "TERMICA" ? "(100×150)" : etqFmt === "A4" ? "(A4)" : etqSub === "CONTINUA" ? "(58 mm contínua)" : "(57×50)"}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
